import { useState } from 'react';
import { Alert, Badge, Button, Form, Modal } from 'react-bootstrap';

import {
  BulkGrantPreview,
  BulkGrantPreviewEntry,
  BulkGrantResult,
  useBulkGrantFlagMutation,
  usePreviewBulkGrantMutation,
} from './featureFlagsApi';
import { serverMessageOf } from './serverMessageOf';

const USER_IDS_INPUT_ID = 'bulk-grant-user-ids';
const DIALOG_TITLE_ID = 'bulk-grant-title';

const STATUS_LABELS: Record<BulkGrantPreviewEntry['status'], string> = {
  willBeGranted: 'will be granted',
  alreadyHolds: 'already holds it',
  noSuchAccount: 'no such account',
  notAUserId: 'not a user id',
};

const PreviewedEntry = ({ entry }: { entry: BulkGrantPreviewEntry }) =>
  entry.status === 'notAUserId' ? (
    <li>{`${entry.entry}: ${STATUS_LABELS[entry.status]}`}</li>
  ) : (
    <li>
      {`${entry.userId}: ${STATUS_LABELS[entry.status]}`}
      {entry.cancelled && (
        <>
          {' '}
          <Badge bg="secondary">cancelled</Badge>
        </>
      )}
      {entry.blocked && (
        <>
          {' '}
          <Badge bg="warning" text="dark">blocked: grant has no effect</Badge>
        </>
      )}
    </li>
  );

const GrantOutcome = ({ result }: { result: BulkGrantResult }) => (
  <Alert variant="success" className="mt-3 mb-0">
    <div>{`Granted to ${result.granted.length} accounts; ${result.alreadyHeld.length} already held it`}</div>
    {result.noSuchAccount.length > 0 && <div>{`No such account: ${result.noSuchAccount.join(', ')}`}</div>}
  </Alert>
);

type BulkGrantModalProps = {
  flag: string;
  onClose: () => void;
};

function BulkGrantModal({ flag, onClose }: BulkGrantModalProps) {
  const [previewBulkGrant] = usePreviewBulkGrantMutation();
  const [bulkGrantFlag] = useBulkGrantFlagMutation();
  const [userIds, setUserIds] = useState('');
  const [previewed, setPreviewed] = useState<{ userIds: string; preview: BulkGrantPreview } | undefined>(undefined);
  const [result, setResult] = useState<BulkGrantResult | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const [granting, setGranting] = useState(false);

  const withdrawPreview = (): void => {
    setPreviewed(undefined);
    setResult(undefined);
  };

  const editUserIds = (edited: string): void => {
    setUserIds(edited);
    withdrawPreview();
  };

  const preview = async (): Promise<void> => {
    setFailure(undefined);
    withdrawPreview();
    const response = await previewBulkGrant({ flag, userIds });
    if ('error' in response) {
      setFailure(serverMessageOf(response.error) ?? 'The preview failed');
    } else {
      setPreviewed({ userIds, preview: response.data });
    }
  };

  const grant = async (previewedUserIds: string): Promise<void> => {
    setFailure(undefined);
    setGranting(true);
    const response = await bulkGrantFlag({ flag, userIds: previewedUserIds });
    setGranting(false);
    if ('error' in response) {
      setFailure(serverMessageOf(response.error) ?? 'The grant failed');
    } else {
      setResult(response.data);
    }
  };

  return (
    <Modal show onHide={onClose} aria-labelledby={DIALOG_TITLE_ID}>
      <Modal.Header closeButton>
        <Modal.Title id={DIALOG_TITLE_ID}>{`Grant ${flag} to accounts`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Label htmlFor={USER_IDS_INPUT_ID}>User ids</Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          id={USER_IDS_INPUT_ID}
          value={userIds}
          onChange={(event) => editUserIds(event.target.value)}
        />
        {previewed?.preview.warning && (
          <Alert variant="warning" className="mt-3 mb-0">
            {`${previewed.preview.warning}: grant has no effect`}
          </Alert>
        )}
        {previewed && (
          <ul aria-label="Preview" className="mt-3 mb-0">
            {previewed.preview.entries.map((entry) => (
              <PreviewedEntry key={'userId' in entry ? entry.userId : entry.entry} entry={entry} />
            ))}
          </ul>
        )}
        {result && <GrantOutcome result={result} />}
        {failure && (
          <Alert variant="danger" className="mt-3 mb-0">
            {failure}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="outline-primary" onClick={preview}>
          Preview
        </Button>
        {previewed && (
          <Button variant="primary" disabled={granting || result !== undefined} onClick={() => grant(previewed.userIds)}>
            Confirm
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default BulkGrantModal;
